import React, {useState, useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';
import {withRouter} from 'react-router-dom';
import {withStyles} from '@material-ui/core/styles';
import {GlobalCss} from 'components/global-css.js';
import {ViewMode} from 'models/view-mode.js';
import {ErrorMessage} from 'components/ErrorMessage.js';
import {FarmSelect} from 'components/FarmSelect.js';
import {sessionState} from 'session/session-state.js';
import {ReturnButton} from 'components/ReturnButton.js';
import Dialog from '@material-ui/core/Dialog';
import Slide from '@material-ui/core/Slide';
import DialogTitle from '@material-ui/core/DialogTitle';

import FormControlLabel from '@material-ui/core/FormControlLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Checkbox from '@material-ui/core/Checkbox';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import Button from '@material-ui/core/Button';
import {eartagAlertConfigAPI} from 'api/eartag-alert-config';
import {ErrorUtil} from 'utils/error-util.js';
import LinearProgress from '@material-ui/core/LinearProgress';

const styles = {
  flexContainer: GlobalCss.flexContainer
};

const EartagAlertConfigEdit = (props) => {
  const {match, history, classes} = props;
  const {action, id} = match.params;
  const farm = sessionState.currentFarm();
  const viewMode = new ViewMode(action);
  const title = `[イヤタグ] アラート設定 - ${viewMode.label}`;
  const buttonColor = viewMode.isDelete() ? 'secondary' : 'primary';
  const notification = `${viewMode.label}が完了しました`;
  const isReadOnly = viewMode.isDelete();
  const isCreateMode = viewMode.isCreate();

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [startHour, setStartHour] = useState(7);
  const [endHour, setEndHour] = useState(19);
  const [alertType, setAlertType] = useState('dropout');
  const [showDialog, setShowDialog] = useState(false);

  const returnAction = () => {
    history.push('/eartag_alert_config');
  };

  const showAction = useCallback(() => {
    if (!id || !farm) {
      return;
    }

    setIsLoading(true);
    return eartagAlertConfigAPI.show(id, farm.id()).then((res) => {
      const data = res.data;
      const settings = data.settings || {};
      const normalizeHour = (value) => {
        if (value === null || value === undefined || value === '') {
          return '';
        }
        const parsed = Number(value);
        return Number.isNaN(parsed) ? '' : parsed;
      };

      setEnabled(Boolean(data.enabled));
      setAlertType(data.alertType);
      setStartHour(normalizeHour(settings.suspendEndHour));
      setEndHour(normalizeHour(settings.suspendStartHour));
      setIsLoading(false);
    }).catch((err) => {
      const message = ErrorUtil.generateMessage(err);
      setErrorMessage(message);
      setIsLoading(false);
    });
  }, [id, farm]);

  useEffect(() => {
    if (isCreateMode) {
      setIsLoading(false);
      return;
    }

    showAction();
  }, [isCreateMode, showAction]);

  const validate = () => {
    const errors = [];

    if (viewMode.isDelete()) {
      return {valid: true, errors};
    }

    const normalizedStartHour = Number(startHour);
    const normalizedEndHour = Number(endHour);

    if (!Number.isInteger(normalizedStartHour) || normalizedStartHour < 0 || normalizedStartHour > 23) {
      errors.push('開始時間を選択してください。');
    }

    if (!Number.isInteger(normalizedEndHour) || normalizedEndHour < 0 || normalizedEndHour > 23) {
      errors.push('終了時間を選択してください。');
    }

    if (errors.length === 0 && normalizedStartHour === normalizedEndHour) {
      errors.push('開始時間と終了時間は異なる時間を指定してください。');
    }

    return {valid: errors.length === 0, errors: errors};
  };

  const save = () => {
    const result = validate();
    if (!result.valid) {
      setErrorMessage(result.errors.join('\n'));
      return;
    }

    setErrorMessage('');
    let action;
    if (viewMode.isCreate()) {
      action = eartagAlertConfigAPI.create(createParams());
    } else if (viewMode.isUpdate()) {
      action = eartagAlertConfigAPI.update(id, farm.id(), createParams());
    } else if (viewMode.isDelete()) {
      action = eartagAlertConfigAPI.delete(id, farm.id());
    }
    setIsLoading(true);

    action.then(() => {
      setIsLoading(false);
      setShowDialog(true);
      setTimeout(() => {
        setShowDialog(false);
        returnAction();
      }, 1000);
    }).catch((error) => {
      setIsLoading(false);
      setErrorMessage(ErrorUtil.generateMessage(error));
    });
  };

  const createParams = () => {
    return {
      farmId: farm.id(),
      enabled: enabled,
      alert_type: alertType,
      settings: {
        suspend_start_hour: Number(endHour),
        suspend_end_hour: Number(startHour),
      },
    };
  };

  return (
    <div style={{marginLeft: '20px'}}>
      <h2>{title}</h2>

      <ul className={classes.flexContainer}>
        <li>
          <ReturnButton returnAction={() => returnAction()} />
        </li>

        <li style={{marginLeft: '500px'}}>
          <Button
            variant="contained"
            color={buttonColor}
            onClick={() => save()}
            disabled={isLoading}>
            {viewMode.label}
          </Button>
        </li>
      </ul>
      <div style={{marginLeft: '40px'}}>
        <ErrorMessage errorMessage={errorMessage} />
      </div>

      <div style={{width: 300, marginBottom: 10, marginLeft: 200}}>
        {isLoading ? <LinearProgress /> : null}
      </div>

      <ul className={classes.flexContainer} style={{marginBottom: 20}}>
        <li>
          <FarmSelect isDisabled={true} />
        </li>
      </ul>

      <ul className={classes.flexContainer} style={{marginBottom: 20}}>
        <li style={{width: 200, marginLeft: 40}}>
          <InputLabel htmlFor="alert-type" style={{fontSize: 12}}>アラート種別</InputLabel>
          <Select
            className={classes.selectField}
            value={alertType}
            onChange={(e) => setAlertType(e.target.value)}
            inputProps={{name: 'alertType', id: 'alert-type'}}
            disabled={isReadOnly}
          >
            <MenuItem value={'dropout'}>脱落</MenuItem>
          </Select>
        </li>
      </ul>
      <ul className={classes.flexContainer} style={{marginBottom: 20}}>
        <li style={{width: 250, marginLeft: 40}}>
          <InputLabel htmlFor="eartag-sensor-enable" style={{fontSize: 12}}>利用設定</InputLabel>
          <FormControlLabel
            control={
              <Checkbox
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                color="primary"
                disabled={isReadOnly}
              />
            }
            label="利用する"
          />
        </li>

      </ul>

      <ul className={classes.flexContainer} style={{marginLeft: 40, marginBottom: 20}}>
        <li>
          <InputLabel htmlFor="start-hour" style={{fontSize: 12}}>開始時間</InputLabel>
          <Select
            style={{marginTop: 5, width: 100}}
            value={startHour}
            onChange={(e) => setStartHour(e.target.value)}
            inputProps={{name: 'startHour', id: 'start-hour'}}
            disabled={isReadOnly}
          >
            {(() => {
              const list = [];
              for (let index = 0; index < 24; index++) {
                list.push(<MenuItem key={`start-hour-${index}`} value={index}>{index}:00</MenuItem>);
              }
              return list;
            })()}
          </Select>
        </li>
        <li><div style={{width: 50, textAlign: 'center', marginTop: 20}}>  ~ </div></li>
        <li>
          <InputLabel htmlFor="end-hour" style={{fontSize: 12}}>終了時間</InputLabel>
          <Select
            style={{marginTop: 5, width: 100}}
            value={endHour}
            onChange={(e) => setEndHour(e.target.value)}
            inputProps={{name: 'endHour', id: 'end-hour'}}
            disabled={isReadOnly}
          >
            {(() => {
              const list = [];
              for (let index = 0; index < 24; index++) {
                list.push(<MenuItem key={`end-hour-${index}`} value={index}>{index}:00</MenuItem>);
              }
              return list;
            })()}
          </Select>
        </li>
      </ul>
      <Dialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        TransitionComponent={Slide}
        aria-labelledby="notification-dialog"
      >
        <DialogTitle id="notification-dialog">{notification}</DialogTitle>
      </Dialog>
    </div>
  );
};

EartagAlertConfigEdit.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      action: PropTypes.string.isRequired,
      id: PropTypes.string,
    }).isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    flexContainer: PropTypes.string.isRequired,
    selectField: PropTypes.string.isRequired,
  }).isRequired,
};

const forExport = withRouter(withStyles(styles)(EartagAlertConfigEdit));
export {forExport as EartagAlertConfigEdit};
